const findSnapshotAdmin = (admins, adminId) => {
  if (adminId == null || !Array.isArray(admins)) return null;
  return admins.find((candidate) => {
    const keys = [
      candidate?.admin_user_id,
      candidate?.id,
      candidate?.user_id,
      candidate?.username,
    ]
      .map((value) => (value == null ? '' : String(value).trim()))
      .filter(Boolean);
    return keys.includes(String(adminId));
  }) || null;
};

const findSnapshotArtist = (artists, artistId) => {
  if (artistId == null || !Array.isArray(artists)) return null;
  return artists.find((candidate) => {
    const keys = [candidate?.artist_id, candidate?.id]
      .map((value) => (value == null ? '' : String(value).trim()))
      .filter(Boolean);
    return keys.includes(String(artistId));
  }) || null;
};

const fallbackAdminEntryFromMessage = (message) => ({
  admin_full_name: message?.admin_full_name,
  admin_profile_picture: message?.admin_profile_picture,
  profile_one_liner: message?.admin_profile_one_liner || '',
  position: message?.admin_position || '',
  department: message?.admin_department || '',
  instagram_url: message?.admin_instagram_url || '',
  twitter_url: message?.admin_twitter_url || '',
  facebook_url: message?.admin_facebook_url || '',
  hp_url: message?.admin_hp_url || '',
});

const fallbackArtistEntryFromMessage = (message) => ({
  artist_id: message?.artist_id,
  artist_name: message?.artist_name,
  artist_profile_picture: message?.artist_profile_picture,
  artist_parts: message?.artist_parts,
  profile_one_liner: message?.artist_profile_one_liner || '',
  instagram_url: message?.artist_instagram_url || '',
  twitter_url: message?.artist_twitter_url || '',
  facebook_url: message?.artist_facebook_url || '',
  hp_url: message?.artist_hp_url || '',
  career_timeline: Array.isArray(message?.artist_career_timeline) ? message.artist_career_timeline : [],
  name_en: message?.artist_name_en || '',
});

export const resolveEventAvatarProfileTarget = ({
  message,
  isAnnouncementMessage,
  participantSnapshot,
  adminLookup,
  participantArtistMap,
  buildAdminProfilePayload,
  buildArtistProfilePayload,
  buildParticipantEventMembershipLabel,
  buildParticipantEventPartSummary,
  selectedEventId,
  effectiveGroupEvents,
  isUnifiedEventGroupChat = false,
  normalizeEventUuidList,
  pickFirstString,
}) => {
  if (!message) return null;
  if (isAnnouncementMessage?.(message)) return null;

  if (message.isAdminMsg) {
    const adminId = message.admin_user_id;
    const snapshotAdmin = findSnapshotAdmin(participantSnapshot?.admins, adminId);
    const entry =
      (adminId != null ? adminLookup?.get(String(adminId)) : null) ||
      fallbackAdminEntryFromMessage(message);

    const eventMembershipLabel = pickFirstString(
      entry?.eventMembershipLabel,
      snapshotAdmin?.eventMembershipLabel
    ) || buildParticipantEventMembershipLabel(
      normalizeEventUuidList(
        snapshotAdmin?.event_uuids || entry?.event_uuids || (selectedEventId ? [selectedEventId] : [])
      )
    );

    const payload = buildAdminProfilePayload({
      ...entry,
      admin_user_id: adminId,
      eventMembershipLabel,
    });
    return payload ? { type: 'admin', payload } : null;
  }

  const snapshotArtist = findSnapshotArtist(participantSnapshot?.artists, message.artist_id);
  const artistEntry =
    (message.artist_id != null ? participantArtistMap?.get(String(message.artist_id)) : null) ||
    fallbackArtistEntryFromMessage(message);

  const profileSource = snapshotArtist
    ? {
        ...artistEntry,
        ...snapshotArtist,
        event_uuids: snapshotArtist?.event_uuids || artistEntry?.event_uuids,
        event_part_assignments: snapshotArtist?.event_part_assignments || artistEntry?.event_part_assignments,
      }
    : artistEntry;

  const partSummary = buildParticipantEventPartSummary?.(profileSource) || {};
  const partLabel = partSummary.partLabel || 'パート未設定';

  const eventMembershipLabel = pickFirstString(
    artistEntry?.eventMembershipLabel,
    snapshotArtist?.eventMembershipLabel
  ) || buildParticipantEventMembershipLabel(
    normalizeEventUuidList(
      snapshotArtist?.event_uuids || artistEntry?.event_uuids || (selectedEventId ? [selectedEventId] : [])
    )
  );

  const eventPartLabel = pickFirstString(
    profileSource?.eventPartLabel,
    snapshotArtist?.eventPartLabel,
    partSummary?.eventPartLabel
  );

  const payload = buildArtistProfilePayload({
    ...profileSource,
    partLabel,
    eventMembershipLabel,
    eventPartLabel,
    group_events: effectiveGroupEvents,
    isUnifiedEventGroupChat,
  });
  return payload ? { type: 'artist', payload } : null;
};

export const createEventAvatarProfileTargetResolver = (options) => (message) =>
  resolveEventAvatarProfileTarget({
    ...options,
    message,
  });
